import React, { useEffect } from 'react';
import { colors } from '../styles/variables';
import CourseHeader from '../components/course/CourseHeader';
import CourseAbout from '../components/course/CourseAbout';
import CourseCurriculum from '../components/course/CourseCurriculum';
import CourseProjects from '../components/course/CourseProjects';
import CourseInstructors from '../components/course/CourseInstructors';
import CourseCta from '../components/course/CourseCta';
import CourseReviewSection from '../components/course/CourseReviewSection';
import Seo from '../components/common/Seo';
import { projectsByCourse } from '../data/projects';

const courseJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'React 프론트엔드',
  description: '최신 트렌드 반영, 실무형 React 프론트엔드 개발을 1:1 멘토링으로 완성하는 과정.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'ElectroWave Club',
    sameAs: 'https://electrowave.kr',
  },
  inLanguage: 'ko',
  educationalLevel: 'intermediate',
  url: 'https://electrowave.kr/react',
};

const ReactPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  // 코스 헤더 데이터
  const headerData = {
    tag: '웹 프론트엔드',
    title: 'React 프론트엔드 과정',
    description: '최신 트렌드를 반영한 React 개발 기법을 배우며 완성도 높은 프론트엔드 개발자로 성장합니다. 컴포넌트 기반 UI 개발부터 상태 관리, 서버 연동까지 실무에서 필요한 모든 기술을 익혀보세요.',
    duration: '총 480시간 / 16주 과정',
    level: '중급자 대상',
    bgColor: colors.reactColor,
    bgImage: '/assets/images/course-thumb-react.png'
  };
  
  // 코스 소개 데이터
  const aboutData = {
    subtitle: '현대적인 웹 프론트엔드 개발의 핵심인 React를 체계적으로 배우는 과정입니다.',
    forWhom: [
      'JavaScript 기초 지식을 갖추고 있는 개발자',
      '웹 프론트엔드 개발자로 취업을 준비하는 분',
      '모던 웹 개발 기술을 습득하고 싶은 분',
      '기존 jQuery 기반 개발에서 SPA 개발로 전환하고 싶은 분',
      'UI/UX에 관심이 있는 디자이너',
      '풀스택 개발자로 성장하고 싶은 분'
    ],
    skills: [
      'React의 핵심 개념과 최신 기술 활용 능력',
      '컴포넌트 기반 UI 설계 및 개발 역량',
      'React Hooks를 활용한 상태 관리',
      'Redux, Context API 등 상태 관리 라이브러리 활용',
      'RESTful API 연동 및 데이터 처리',
      '성능 최적화 및 테스트 기법 적용',
      'React 기반 프로젝트 배포 및 운영'
    ],
    imageSrc: '/assets/images/course-thumb-react.png'
  };
  
  // 커리큘럼 데이터
  const curriculumData = {
    subtitle: 'React 초보자부터 전문가까지 성장할 수 있는 체계적인 커리큘럼을 제공합니다.',
    modules: [
      {
        title: '1. React 소개 및 개발 환경 설정',
        description: 'React의 기본 개념과 개발 환경을 구축하는 방법을 배웁니다.',
        lessons: [
          'React의 특징과 장점',
          'Node.js 및 npm 설치',
          'Create React App으로 프로젝트 생성',
          'VSCode 등 개발 도구 설정',
          'React Developer Tools 활용법'
        ]
      },
      {
        title: '2. JSX와 React 기초',
        description: 'React의 기본 문법인 JSX를 배우고 컴포넌트의 개념을 이해합니다.',
        lessons: [
          'JSX 문법과 특징',
          '엘리먼트와 렌더링',
          '컴포넌트 개념 이해',
          '함수형 컴포넌트와 클래스형 컴포넌트',
          'Props와 State의 개념'
        ]
      },
      {
        title: '3. React Hooks 활용',
        description: 'React 16.8부터 도입된 Hooks를 활용한 상태 관리 방법을 학습합니다.',
        lessons: [
          'useState로 상태 관리하기',
          'useEffect로 생명주기 다루기',
          'useContext, useReducer 활용',
          '커스텀 Hooks 만들기',
          'Hooks의 규칙과 모범 사례'
        ]
      },
      {
        title: '4. 라우팅과 SPA 구현',
        description: 'React Router를 활용하여 단일 페이지 애플리케이션(SPA)을 구현하는 방법을 배웁니다.',
        lessons: [
          'React Router 설치 및 기본 설정',
          'Route와 Link 컴포넌트 활용',
          '동적 라우팅 구현',
          '중첩 라우팅 및 레이아웃 구성',
          '인증에 따른 보호된 라우트 구현'
        ]
      },
      {
        title: '5. 상태 관리 라이브러리',
        description: '대규모 애플리케이션을 위한 상태 관리 라이브러리 활용법을 학습합니다.',
        lessons: [
          'Redux의 개념과 구조',
          'Redux 액션, 리듀서, 스토어 구현',
          'React-Redux와 Redux Hooks',
          'Redux Toolkit 활용',
          'Context API와 비교 및 적절한 사용법'
        ]
      },
      {
        title: '6. API 연동 및 비동기 처리',
        description: '서버와의 통신 및 비동기 데이터 처리 방법을 배웁니다.',
        lessons: [
          'Axios를 활용한 HTTP 요청',
          'Async/Await와 Promise 활용',
          'React Query를 이용한 데이터 페칭',
          '에러 처리 및 로딩 상태 관리',
          'API 인증 및 보안 처리'
        ]
      },
      {
        title: '7. 성능 최적화 및 배포',
        description: 'React 애플리케이션의 성능을 최적화하고 배포하는 방법을 학습합니다.',
        lessons: [
          'React.memo와 useMemo, useCallback',
          '코드 스플리팅과 지연 로딩',
          'Lighthouse를 이용한 성능 측정',
          'Netlify, Vercel 등을 이용한 배포',
          'CI/CD 파이프라인 구축'
        ]
      }
    ]
  };
  
  // 프로젝트 데이터
  const projectsData = {
    subtitle: '학습한 내용을 바로 적용할 수 있는 실전형 React 프로젝트를 통해 포트폴리오를 구축합니다.',
    projects: projectsByCourse.react,
  };
  
  // 강사진 데이터
  const instructorsData = {
    subtitle: 'React와 프론트엔드 개발 분야의 전문가들이 여러분의 성장을 돕습니다.',
    instructors: [
      {
        name: 'Rechard Park',
        title: '시니어 프론트엔드 개발자',
        bio: '대기업 프론트엔드 개발 팀장 출신으로, 6년 이상의 React 개발 경험을 보유하고 있습니다. 복잡한 웹 애플리케이션의 설계와 개발에 전문성을 갖추고 있으며, 오픈소스 React 라이브러리 메인테이너로 활동 중입니다.',
        image: '/assets/images/instructor-park-jiwon.png'
      },
      {
        name: 'Lisa Choi',
        title: '프론트엔드 아키텍트',
        bio: '대형 숙박 플랫폼 프론트엔드 개발 경험이 풍부하며, 마이크로 프론트엔드 아키텍처와 성능 최적화 분야에서 뛰어난 실력을 보유하고 있습니다. React 생태계에 깊은 이해를 가진 실무 중심 멘토입니다.',
        image: '/assets/images/instructor-choi-yujin.png'
      }
    ]
  };
  
  // CTA 데이터
  const ctaData = {
    title: 'React로 현대적인 웹 개발을 시작하세요',
    subtitle: 'ElectroWave Club의 React 과정을 통해 사용자 경험을 중시하는 프론트엔드 개발자로 성장하세요.',
    bgColor: colors.reactColor,
    endColor: '#0D47A1'  // 진한 파란색
  };
  
  return (
    <>
      <Seo
        title="React 프론트엔드"
        description="최신 트렌드 반영, 실무형 React 프론트엔드 개발을 1:1 멘토링으로 완성하는 과정."
        path="/react"
        ogImage="/assets/images/course-thumb-react.png"
        ogType="article"
        jsonLd={courseJsonLd}
      />
      <CourseHeader {...headerData} />
      <CourseAbout {...aboutData} />
      <CourseCurriculum {...curriculumData} />
      <CourseProjects {...projectsData} courseSlug="react" />
      <CourseInstructors {...instructorsData} />
      <CourseReviewSection courseTitle="React 프론트엔드" />
      <CourseCta {...ctaData} />
    </>
  );
};

export default ReactPage;
